/**
 * <sample-builder> — cascading sample picker.
 *
 * Reads product data inlined by sections/sample-builder.liquid, lets the customer refine
 * Product -> option1 -> option2 -> option3 down to a concrete variant SKU, collect up to N of
 * them, then POST contact + shipping + the resolved SKUs as JSON to the configured endpoint.
 *
 * No build step / framework — kept as a plain custom element to match the theme's conventions.
 */
class SampleBuilder extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    this.max = parseInt(this.dataset.max, 10) || 5;
    this.endpoint = this.normaliseEndpoint(this.dataset.endpoint);
    this.selected = [];
    this.currentProductIndex = null;
    this.currentValues = [];

    try {
      const raw = this.querySelector('.sample-builder__data').textContent;
      this.products = JSON.parse(raw);
    } catch (err) {
      console.error('[sample-builder] could not parse product data', err);
      this.products = [];
    }

    // Cache elements
    this.productSelect = this.querySelector('[data-role="product"]');
    this.optionControls = Array.from(this.querySelectorAll('[data-role="option-control"]'));
    this.optionSelects = Array.from(this.querySelectorAll('select[data-role="option"]'));
    this.optionLabels = this.optionControls.map((c) => c.querySelector('[data-role="option-label"]'));
    this.resolvedEl = this.querySelector('[data-role="resolved"]');
    this.confirmEl = this.querySelector('[data-role="confirm"]');
    this.confirmThumb = this.querySelector('[data-role="confirm-thumb"]');
    this.hintEl = this.querySelector('[data-role="hint"]');
    this.addButton = this.querySelector('[data-role="add"]');
    this.pickerEl = this.querySelector('[data-role="picker"]');
    this.pickerFullEl = this.querySelector('[data-role="picker-full"]');
    this.selectedWrapper = this.querySelector('[data-role="selected-wrapper"]');
    this.countEl = this.querySelector('[data-role="count"]');
    this.listEl = this.querySelector('[data-role="list"]');
    this.form = this.querySelector('[data-role="form"]');
    this.statusEl = this.querySelector('[data-role="status"]');
    this.submitButton = this.querySelector('[data-role="submit"]');
    this.submitText = this.querySelector('[data-role="submit-text"]');
    this.submitBaseLabel = this.submitText ? this.submitText.textContent : 'Submit';

    // Events
    this.productSelect.addEventListener('change', () => this.onProductChange());
    this.optionSelects.forEach((sel, i) => {
      sel.setAttribute('aria-label', 'Option ' + (i + 1));
      sel.addEventListener('change', () => this.onOptionChange(i));
    });
    this.addButton.addEventListener('click', () => this.addSample());
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.listEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove]');
      if (btn) this.removeSample(parseInt(btn.dataset.remove, 10));
    });

    this.renderSelected();
    this.updateResolved();
  }

  /**
   * Normalise the configured endpoint. Django serves trailing-slash URLs; a POST
   * to the slash-less path hits an unrelated GET-only catch-all and 405s. So we
   * ensure a trailing slash on the path (before any query/hash) to make submission
   * robust to a missing "/" in the theme setting. Blank stays blank (preview mode).
   */
  normaliseEndpoint(raw) {
    const url = (raw || '').trim();
    if (!url) return '';
    // Split off ?query / #hash so we only touch the path.
    const match = url.match(/^([^?#]*)([?#].*)?$/);
    let path = match[1];
    const suffix = match[2] || '';
    if (path && !path.endsWith('/')) path += '/';
    return path + suffix;
  }

  /** Best image for a variant: its own, else the product's. */
  variantImage(variant, product) {
    return (variant && variant.image) || (product && product.image) || '';
  }

  get currentProduct() {
    return this.currentProductIndex == null ? null : this.products[this.currentProductIndex];
  }

  /** Variants we can actually ship as a sample: must carry a SKU. */
  usableVariants(product) {
    return (product.variants || []).filter((v) => v.sku && String(v.sku).trim() !== '');
  }

  /** Variants of the current product whose option values match every already-chosen value below `upto`. */
  matchingVariants(upto) {
    const product = this.currentProduct;
    if (!product) return [];
    return this.usableVariants(product).filter((v) => {
      for (let i = 0; i < upto; i++) {
        if (this.currentValues[i] != null && v.values[i] !== this.currentValues[i]) return false;
      }
      return true;
    });
  }

  onProductChange() {
    const idx = this.productSelect.value;
    this.currentProductIndex = idx === '' ? null : parseInt(idx, 10);
    this.currentValues = [];
    this.buildOptionSelects();
    this.updateResolved();
  }

  /** Show one select per option the product has; populate the first; hide the rest. */
  buildOptionSelects() {
    const product = this.currentProduct;
    const optionCount = product ? product.options.length : 0;

    this.optionControls.forEach((control, i) => {
      if (i < optionCount) {
        this.optionLabels[i].textContent = product.options[i];
        control.hidden = false;
        this.populateOption(i);
      } else {
        control.hidden = true;
        this.optionSelects[i].innerHTML = '';
      }
    });
  }

  /** Fill option select `i` with the distinct values still reachable given lower selections. */
  populateOption(i) {
    const select = this.optionSelects[i];
    const variants = this.matchingVariants(i);
    const seen = [];
    variants.forEach((v) => {
      const val = v.values[i];
      if (val != null && seen.indexOf(val) === -1) seen.push(val);
    });

    const placeholderText = select.querySelector('option[value=""]')
      ? select.querySelector('option[value=""]').textContent
      : 'Select…';

    select.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholderText;
    select.appendChild(ph);

    seen.forEach((val) => {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val;
      select.appendChild(opt);
    });

    // Auto-select when only one choice remains.
    if (seen.length === 1) {
      select.value = seen[0];
      this.currentValues[i] = seen[0];
    } else {
      select.value = '';
      this.currentValues[i] = null;
    }
  }

  onOptionChange(i) {
    this.currentValues[i] = this.optionSelects[i].value || null;
    // Reset everything below this level, then repopulate the next select.
    for (let j = i + 1; j < this.currentValues.length; j++) this.currentValues[j] = null;

    const optionCount = this.currentProduct ? this.currentProduct.options.length : 0;
    for (let j = i + 1; j < optionCount; j++) {
      if (this.currentValues[i] != null) {
        this.populateOption(j);
      } else {
        // parent cleared — blank out descendants
        const sel = this.optionSelects[j];
        const phText = sel.querySelector('option[value=""]')
          ? sel.querySelector('option[value=""]').textContent
          : 'Select…';
        sel.innerHTML = '';
        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = phText;
        sel.appendChild(ph);
        this.currentValues[j] = null;
      }
    }
    this.updateResolved();
  }

  /** The single variant fully matching the current selections, if any. */
  resolveVariant() {
    const product = this.currentProduct;
    if (!product) return null;
    if (this.currentValues.length < product.options.length) return null;
    for (let i = 0; i < product.options.length; i++) {
      if (this.currentValues[i] == null) return null;
    }
    return this.usableVariants(product).find((v) =>
      product.options.every((_, i) => v.values[i] === this.currentValues[i])
    ) || null;
  }

  updateResolved() {
    const variant = this.resolveVariant();
    this.updateHint(variant);

    // Nothing fully resolved yet — keep the confirm row hidden so the page stays calm.
    if (!variant) {
      this.confirmEl.hidden = true;
      return;
    }

    // A full variant resolved: reveal the inline "thumbnail + SKU + Add" row as a clear call to action.
    this.confirmEl.hidden = false;

    const img = this.variantImage(variant, this.currentProduct);
    if (img) {
      this.confirmThumb.src = img;
      this.confirmThumb.hidden = false;
    } else {
      this.confirmThumb.hidden = true;
      this.confirmThumb.removeAttribute('src');
    }

    const alreadyAdded = this.selected.some((s) => s.sku === variant.sku);

    if (alreadyAdded) {
      this.resolvedEl.classList.add('is-warning');
      this.resolvedEl.innerHTML =
        '<span class="sample-builder__resolved-label">Already in your list</span>' +
        '<span class="sample-builder__sku">' + escapeHtml(variant.sku) + '</span>';
      this.addButton.hidden = true;
    } else {
      this.resolvedEl.classList.remove('is-warning');
      this.resolvedEl.innerHTML =
        '<span class="sample-builder__resolved-label">Ready to add</span>' +
        '<span class="sample-builder__sku">' + escapeHtml(variant.sku) + '</span>';
      this.addButton.hidden = false;
      this.addButton.disabled = false;
    }
  }

  /** One-line nudge under the dropdowns telling first-timers what to pick next. */
  updateHint(variant) {
    const product = this.currentProduct;

    if (!product) {
      this.hintEl.hidden = false;
      this.hintEl.textContent = 'Start by choosing a product above.';
      return;
    }
    // Resolved — the confirm bar takes over, so the hint steps aside.
    if (variant) {
      this.hintEl.hidden = true;
      return;
    }
    const remaining = product.options.filter((_, i) => this.currentValues[i] == null);
    if (remaining.length === 0) {
      this.hintEl.hidden = true;
      return;
    }
    this.hintEl.hidden = false;
    this.hintEl.textContent = 'Now choose ' + this.joinNicely(remaining.map((n) => n.toLowerCase())) + '.';
  }

  joinNicely(arr) {
    if (arr.length <= 1) return arr[0] || '';
    return arr.slice(0, -1).join(', ') + ', then ' + arr[arr.length - 1];
  }

  addSample() {
    const variant = this.resolveVariant();
    const product = this.currentProduct;
    if (!variant || !product || this.selected.length >= this.max) return;
    if (this.selected.some((s) => s.sku === variant.sku)) return;

    const options = {};
    product.options.forEach((name, i) => {
      options[name] = this.currentValues[i];
    });

    this.selected.push({
      sku: variant.sku,
      product: product.title,
      variant: variant.title,
      image: this.variantImage(variant, product),
      options: options,
    });

    this.renderSelected();

    // Briefly highlight the row that was just added.
    const justAdded = this.listEl.lastElementChild;
    if (justAdded) {
      justAdded.classList.add('is-new');
      setTimeout(() => justAdded.classList.remove('is-new'), 1500);
    }

    // Start a fresh selection for the next sample, right back at "Select a product…".
    this.resetPicker();
    if (!this.pickerEl.hidden) this.productSelect.focus();
  }

  removeSample(index) {
    this.selected.splice(index, 1);
    this.renderSelected();
    this.updateResolved();
  }

  /** Full reset of the picker, including the product dropdown, ready for the next sample. */
  resetPicker() {
    this.currentValues = [];
    this.productSelect.value = '';
    this.onProductChange();
  }

  renderSelected() {
    this.countEl.textContent = String(this.selected.length);
    this.selectedWrapper.hidden = this.selected.length === 0;
    this.listEl.innerHTML = '';

    this.selected.forEach((item, i) => {
      const li = document.createElement('li');
      li.className = 'sample-builder__item';

      if (item.image) {
        const img = document.createElement('img');
        img.className = 'sample-builder__item-thumb';
        img.src = item.image;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        li.appendChild(img);
      }

      const text = document.createElement('span');
      text.className = 'sample-builder__item-text';
      const optsLabel = Object.values(item.options).filter(Boolean).join(' / ');
      text.innerHTML =
        '<strong>' + escapeHtml(item.product) + '</strong>' +
        (optsLabel ? ' — ' + escapeHtml(optsLabel) : '') +
        '<span class="sample-builder__sku">' + escapeHtml(item.sku) + '</span>';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'sample-builder__remove';
      remove.dataset.remove = String(i);
      remove.setAttribute('aria-label', 'Remove ' + item.product);
      remove.textContent = '✕';

      li.appendChild(text);
      li.appendChild(remove);
      this.listEl.appendChild(li);
    });

    // Submit button reflects the count and stays dimmed until there's something to send.
    const count = this.selected.length;
    this.submitButton.disabled = count === 0;
    this.submitText.textContent =
      count === 0
        ? this.submitBaseLabel
        : 'Submit ' + count + ' sample request' + (count > 1 ? 's' : '');

    // When the list is full, replace the picker with a short note.
    const atMax = count >= this.max;
    this.pickerEl.hidden = atMax;
    this.pickerFullEl.hidden = !atMax;
    if (atMax) {
      this.pickerFullEl.textContent =
        "You've added the maximum of " + this.max + ' samples. Remove one to swap it out.';
    }
  }

  collectField(name) {
    const el = this.form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }

  onSubmit(event) {
    event.preventDefault();
    this.clearStatus();

    if (this.selected.length === 0) {
      this.showStatus('error', 'Please add at least one sample before submitting.');
      return;
    }

    // Required-field check (mirrors the `required` attributes in the markup).
    const required = ['first_name', 'last_name', 'email', 'phone', 'address1', 'city', 'state', 'postcode', 'country'];
    const missing = required.filter((n) => this.collectField(n) === '');
    if (missing.length) {
      this.showStatus('error', 'Please complete all required fields.');
      const first = this.form.querySelector('[name="' + missing[0] + '"]');
      if (first) first.focus();
      return;
    }

    const payload = {
      source: 'sample-builder',
      page_url: window.location.href,
      items: this.selected,
      contact: {
        first_name: this.collectField('first_name'),
        last_name: this.collectField('last_name'),
        company: this.collectField('company'),
        email: this.collectField('email'),
        phone: this.collectField('phone'),
      },
      shipping: {
        address1: this.collectField('address1'),
        address2: this.collectField('address2'),
        city: this.collectField('city'),
        state: this.collectField('state'),
        postcode: this.collectField('postcode'),
        country: this.collectField('country'),
      },
      marketing_opt_in: !!this.form.querySelector('[name="marketing_opt_in"]:checked'),
    };

    // Preview mode: no endpoint configured yet — show what would be sent.
    if (!this.endpoint) {
      console.info('[sample-builder] preview payload (no endpoint configured):', payload);
      this.showStatus('success', 'Preview mode: your request looks good. ' + payload.items.length + ' sample(s) captured. Configure the endpoint URL to enable sending.');
      return;
    }

    this.setSubmitting(true);
    fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json().catch(() => ({}));
      })
      .then((data) => {
        const ref = data && data.reference ? ' (ref ' + data.reference + ')' : '';
        this.showStatus('success', 'Thanks! Your sample request has been received' + ref + '. We\'ll be in touch shortly.');
        this.resetAll();
      })
      .catch((err) => {
        console.error('[sample-builder] submit failed', err);
        this.showStatus('error', 'Sorry, something went wrong sending your request. Please try again or contact us.');
      })
      .finally(() => this.setSubmitting(false));
  }

  setSubmitting(isSubmitting) {
    this.submitButton.disabled = isSubmitting;
    this.submitButton.classList.toggle('is-loading', isSubmitting);
  }

  resetAll() {
    this.selected = [];
    this.productSelect.value = '';
    this.onProductChange();
    this.renderSelected();
    this.form.reset();
  }

  clearStatus() {
    this.statusEl.hidden = true;
    this.statusEl.textContent = '';
    this.statusEl.classList.remove('is-success', 'is-error');
  }

  showStatus(type, message) {
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    this.statusEl.classList.toggle('is-success', type === 'success');
    this.statusEl.classList.toggle('is-error', type === 'error');
    this.statusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

if (!customElements.get('sample-builder')) {
  customElements.define('sample-builder', SampleBuilder);
}
